import React, { type FC, type ReactNode } from 'react';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import SectionMessage from '@atlaskit/section-message/section-message';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import AkSpinner from '@atlaskit/spinner/spinner';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useIsProductAdmin } from '@confluence/current-user/entry-points/useIsProductAdmin.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { LoadableLazy } from '@confluence/loadable';

import { AdminPageLayout } from '../AdminPageLayout/AdminPageLayout.tsx';
import { RestrictedPage } from '../SitePermissions/RestrictedPage.tsx';

const UpsellBannerPlaceholder: FC = () => (
	<SectionMessage>
		<Stack space="space.100">
			<Skeleton width="40%" height={token('space.300')} />
			<Skeleton width="100%" height={token('space.200')} />
			<Skeleton width="80%" height={token('space.200')} />
		</Stack>
	</SectionMessage>
);

const ChangeEditionSectionMessageAdminGlobalV2Loadable = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ChangeEditionSectionMessageAdminGlobalV2" */ '@confluence/change-edition/entry-points/ChangeEditionSectionMessageAdminGlobalV2.tsx'
			)
		).ChangeEditionSectionMessageAdminGlobalV2,
	loading: UpsellBannerPlaceholder,
});

const ChangeEditionSectionMessageAdminSpaceV2Loadable = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ChangeEditionSectionMessageAdminSpaceV2" */ '@confluence/change-edition/entry-points/ChangeEditionSectionMessageAdminSpaceV2.tsx'
			)
		).ChangeEditionSectionMessageAdminSpaceV2,
	loading: UpsellBannerPlaceholder,
});

const styles = cssMap({
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		padding: token('space.1000'),
	},
	headerContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		marginBlockEnd: token('space.300'),
	},
	bannerContainer: {
		marginBlockEnd: token('space.500'),
	},
});

export type AdminSecurityPageBannerVariant = 'global' | 'space';

type AdminSecurityPageWrapperProps = {
	title?: ReactNode;
	documentTitle?: string;
	bannerVariant?: AdminSecurityPageBannerVariant;
	titleAction?: ReactNode;
	fullWidth?: boolean;
	children: ReactNode;
};

export const AdminSecurityPageWrapper: FC<AdminSecurityPageWrapperProps> = ({
	title,
	documentTitle,
	bannerVariant = 'global',
	titleAction,
	fullWidth,
	children,
}) => {
	const { edition } = useSessionData();
	const { isProductAdmin, isAdminCheckLoading } = useIsProductAdmin();
	const isFreeEdition = edition === ConfluenceEdition.FREE;

	if (isAdminCheckLoading) {
		return (
			<Box xcss={styles.spinnerContainer}>
				<AkSpinner size="large" />
			</Box>
		);
	}

	if (!isProductAdmin) {
		return <RestrictedPage />;
	}

	const banner = isFreeEdition ? (
		<Box xcss={styles.bannerContainer}>
			{bannerVariant === 'space' ? (
				<ChangeEditionSectionMessageAdminSpaceV2Loadable />
			) : (
				<ChangeEditionSectionMessageAdminGlobalV2Loadable />
			)}
		</Box>
	) : undefined;

	return (
		<AdminPageLayout banner={banner} fullWidth={fullWidth}>
			{documentTitle !== undefined && <DocumentContentTitle titlePrefix={documentTitle} />}
			{title !== undefined && (
				<Box xcss={styles.headerContainer}>
					<Heading as="h1" size="large">
						{title}
					</Heading>
					{titleAction}
				</Box>
			)}
			{children}
		</AdminPageLayout>
	);
};
